import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReplayIcon from '@mui/icons-material/Replay';
import BoltIcon from '@mui/icons-material/Bolt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import PaymentIcon from '@mui/icons-material/Payment';

import { PageHeader, ResponsiveTable, StatsCard, EmptyState } from '../components/common';
import type { Column } from '../components/common/ResponsiveTable';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import { formatDate } from '../utils/dateUtils';
import digipayAdminService, {
  DP_DESTINATION_LABELS,
  DP_HTTP_CODES,
  DP_INSTRUMENT_COLORS,
  DP_INSTRUMENT_LABELS,
  DP_PSP_CODES,
  DP_REFUND_STATUS_COLORS,
  DP_REFUND_STATUS_LABELS,
  DP_RESULT_CODES,
  DP_TICKET_TYPE_LABELS,
  type DigipayConnectionTest,
  type DigipayInstrument,
  type DigipayLog,
  type DigipayOrderDetail,
  type DigipayOrderRow,
  type DigipayOverview,
  type DigipayQuote,
  type DigipayRefundList,
  type DigipayRefundListRow,
  DigipayProbeTicket,
} from '../services/digipay.service';
import RefundPreviewSteps, { RefundPreviewData } from '../components/common/RefundPreviewSteps';
import { adminRefundPreviewService } from '../services/digipay.service';

// ---------------------------------------------------------------- helpers

type RefundRow = DigipayRefundListRow & { _id: string };

const DP_BLUE = '#1159FC';
const fmtToman = (n?: number | null): string =>
  n === undefined || n === null || Number.isNaN(Number(n)) ? '—' : `${Math.round(Number(n)).toLocaleString('fa-IR')} تومان`;
const fa = (v: any) => toPersianDigits(String(v ?? ''));

const InstrumentChip: React.FC<{ instrument?: string | null; size?: 'small' | 'medium' }> = ({ instrument, size = 'small' }) => {
  const s = instrument || 'unknown';
  const color = DP_INSTRUMENT_COLORS[s] || DP_INSTRUMENT_COLORS.unknown;
  return <Chip size={size} label={DP_INSTRUMENT_LABELS[s] || s} sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 700, fontSize: 11.5 }} />;
};

const RefundStatusChip: React.FC<{ status: number }> = ({ status }) => {
  const color = DP_REFUND_STATUS_COLORS[status] || '#64748B';
  return <Chip size="small" label={DP_REFUND_STATUS_LABELS[status] || String(status)} sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 700, fontSize: 11 }} />;
};

const Mono: React.FC<{ children?: React.ReactNode; copy?: string }> = ({ children, copy }) => (
  <Box component="span" sx={{ direction: 'ltr', unicodeBidi: 'plaintext', fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    {children}
    {copy && (
      <Tooltip title="کپی">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(copy); }} sx={{ p: 0.25 }}>
          <ContentCopyIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6, borderBottom: '1px dashed', borderColor: 'divider', fontSize: 13 }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ textAlign: 'left', fontWeight: 600, fontSize: 13, minWidth: 0 }}>{value}</Box>
  </Box>
);

const DpLogo: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <img src="/digipay-logo.png" alt="دیجی‌پی" width={size} height={size} style={{ borderRadius: Math.round(size * 0.25), display: 'block' }} />
);

const LOG_ACTION_LABELS: Record<string, string> = { request: 'درخواست تیکت', callback: 'کال‌بک', verify: 'تأیید پرداخت (verify)', refund: 'بازگشت وجه (refund/reverse/کارمزد)', inquiry: 'استعلام ریفاند' };
const LOG_STATUS_COLORS: Record<string, string> = { success: '#16A34A', failed: '#DC2626', pending: '#D97706' };

const FIELD_LABELS: Record<string, string> = {
  providerId: 'شناسهٔ خرید (providerId)', trackingCode: 'کد پیگیری', refundProviderId: 'شناسهٔ ریفاند', saleTrackingCode: 'کد پیگیری خرید', ticket: 'تیکت',
  orderNumber: 'شمارهٔ سفارش', orderId: 'شناسهٔ سفارش', amount: 'مبلغ', baseAmount: 'مبلغ سفارش (کالا)', surchargeAmount: 'کارمزد', surchargePercent: 'درصد کارمزد',
  surchargeMode: 'مبنای کارمزد', payableAmount: 'مبلغ قابل پرداخت', verifiedAmount: 'مبلغ تأییدشده', paidAmount: 'مبلغ پرداختی', alreadyRefunded: 'قبلاً برگشت‌داده‌شده',
  surchargeAdjustedAmount: 'کارمزد برگشتی', instrument: 'ابزار انتخابی', actualInstrument: 'ابزار واقعی', ticketType: 'نوع تیکت', preferredGateway: 'درگاه ترجیحی',
  basketItems: 'تعداد اقلام سبد', paymentGateway: 'درگاه (paymentGateway)', pspCode: 'کد PSP', pspName: 'PSP', rrn: 'RRN', maskedPan: 'کارت', terminalId: 'ترمینال',
  gatewayFee: 'هزینهٔ درگاه (برآورد)', feeEstimated: 'کارمزد برآوردی', context: 'زمینه', adminId: 'ادمین', reason: 'دلیل', kind: 'نوع عملیات', full: 'کامل',
  result: 'نتیجه', status: 'وضعیت', message: 'پیام', code: 'کد', type: 'نوع', isCredit: 'اعتباری', psp: 'PSP', order: 'سفارش', dl: 'دیپ‌لینک', redirectUrl: 'آدرس درگاه',
  errors: 'خطاها', ip: 'IP', userAgent: 'مرورگر', title: 'عنوان', level: 'سطح', fpCode: 'کد تأمین‌کنندهٔ اعتبار', fpName: 'تأمین‌کنندهٔ اعتبار', additionalInfo: 'اطلاعات تکمیلی',
  prepaymentAmount: 'پیش‌پرداخت + کارمزد', cashAmount: 'مبلغ نقدی', creditAmount: 'مبلغ اعتباری', instantFinalization: 'فاینال فوری قرارداد', generateInvoice: 'صدور فاکتور',
  transferDate: 'تاریخ انتقال', destinationType: 'نوع مقصد', destination: 'مقصد', resultCode: 'کد نتیجه',
};
const AMOUNT_KEYS = /amount|toman|refund(ed)?$|^fee|price/i;

function formatLogValue(key: string, v: any, rial: boolean): React.ReactNode {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'بله' : 'خیر';
  if (key === 'instrument' || key === 'actualInstrument') return DP_INSTRUMENT_LABELS[String(v)] || String(v);
  if (key === 'ticketType' || key === 'type') return DP_TICKET_TYPE_LABELS[Number(v)] || String(v);
  if (key === 'destinationType') return DP_DESTINATION_LABELS[Number(v)] || String(v);
  if (typeof v === 'number') {
    if (AMOUNT_KEYS.test(key) && !/percent|count|code|status|id$|type/i.test(key)) return `${v.toLocaleString('fa-IR')} ${rial ? 'ریال' : 'تومان'}`;
    if (/percent/i.test(key)) return `${v.toLocaleString('fa-IR')}٪`;
    if (key === 'transferDate' && v > 1e12) return formatDate(new Date(v).toISOString());
    return v.toLocaleString('fa-IR');
  }
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return formatDate(v);
    if (/^https?:\/\//.test(v) || /^[A-Za-z0-9_:\-]{16,}$/.test(v)) return <Mono copy={v}>{v}</Mono>;
    return v;
  }
  return null;
}

const LogKV: React.FC<{ obj: any; rial?: boolean; depth?: number }> = ({ obj, rial = false, depth = 0 }) => {
  if (obj === null || obj === undefined) return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (Array.isArray(obj)) {
    return (
      <Stack spacing={0.75} sx={{ pr: depth ? 1.5 : 0 }}>
        {obj.map((item, i) => (
          <Box key={i} sx={{ p: 1, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>مورد {fa(i + 1)}</Typography>
            {typeof item === 'object' ? <LogKV obj={item} rial={rial} depth={depth + 1} /> : <Typography variant="body2">{String(item)}</Typography>}
          </Box>
        ))}
      </Stack>
    );
  }
  if (typeof obj !== 'object') return <Typography variant="body2">{String(obj)}</Typography>;
  const entries = Object.entries(obj).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Box sx={{ pr: depth ? 1.5 : 0, borderRight: depth ? '2px solid' : 'none', borderColor: 'divider' }}>
      {entries.map(([k, v]) => {
        const label = FIELD_LABELS[k] || k;
        const simple = formatLogValue(k, v, rial);
        if (simple !== null) {
          return (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: '1px dashed', borderColor: 'divider', fontSize: 12.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
              <Box sx={{ textAlign: 'left', fontWeight: 600, fontSize: 12.5, minWidth: 0, wordBreak: 'break-word' }}>{simple}</Box>
            </Box>
          );
        }
        return (
          <Box key={k} sx={{ py: 0.75 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{label}</Typography>
            <LogKV obj={v} rial={rial} depth={depth + 1} />
          </Box>
        );
      })}
    </Box>
  );
};

const LogDetails: React.FC<{ log: DigipayLog; amountUnit: string }> = ({ log, amountUnit }) => {
  const rial = amountUnit === 'rial';
  const sections: Array<{ title: string; obj: any; rial?: boolean; hint?: string }> = [
    { title: 'خلاصه', obj: { status: log.status === 'success' ? 'موفق' : log.status === 'failed' ? 'ناموفق' : 'در انتظار', message: log.message, amount: log.amount, providerId: log.authority, trackingCode: log.refId, code: log.code, ...(log.error ? { errors: log.error } : {}) } },
    { title: 'اطلاعات عملیات', obj: log.meta },
    { title: 'درخواست ارسالی به دیجی‌پی', obj: log.requestBody },
    { title: 'پاسخ دیجی‌پی', obj: log.responseBody, rial, hint: rial ? 'مبالغ پاسخ دیجی‌پی به ریال هستند' : undefined },
    { title: 'دادهٔ بازگشتی از درگاه (کال‌بک)', obj: log.callbackQuery, rial, hint: rial ? 'مبلغ کال‌بک به ریال است' : undefined },
  ].filter((x) => x.obj && (typeof x.obj !== 'object' || Object.keys(x.obj).length > 0));
  return (
    <Grid container spacing={1.5}>
      {sections.map((sec) => (
        <Grid item xs={12} md={6} key={sec.title}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>{sec.title}</Typography>
            {sec.hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{sec.hint}</Typography>}
            <LogKV obj={sec.obj} rial={sec.rial} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

// ---------------------------------------------------------------- page

const DigipayCenter: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = Number(searchParams.get('tab') || (searchParams.get('search') ? 1 : 0)) || 0;
  const [tab, setTab] = useState<number>(initialTab);

  // ── overview ──
  const [overview, setOverview] = useState<DigipayOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      setOverview(await digipayAdminService.overview());
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت نمای کلی دیجی‌پی', { variant: 'error' });
    } finally {
      setOverviewLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => { loadOverview(); }, [loadOverview]);
  const amountUnit = overview?.amountUnit || 'rial';

  // ── orders ──
  const [orders, setOrders] = useState<DigipayOrderRow[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [instrumentFilter, setInstrumentFilter] = useState<string>('');
  const [filter, setFilter] = useState<string>(searchParams.get('filter') || '');
  const [search, setSearch] = useState<string>(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState<string>(searchParams.get('search') || '');

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const r = await digipayAdminService.listOrders({ page: page + 1, limit: rowsPerPage, instrument: instrumentFilter, filter, search });
      setOrders(r?.docs || []);
      setOrdersTotal(r?.total || 0);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت سفارش‌های دیجی‌پی', { variant: 'error' });
    } finally {
      setOrdersLoading(false);
    }
  }, [page, rowsPerPage, instrumentFilter, filter, search, enqueueSnackbar]);
  useEffect(() => { if (tab === 1) loadOrders(); }, [tab, loadOrders]);

  // ── detail dialog ──
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DigipayOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      setDetail(await digipayAdminService.orderDetail(id));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت جزئیات سفارش', { variant: 'error' });
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => { if (detailId) loadDetail(detailId); else setDetail(null); }, [detailId, loadDetail]);

  const [confirm, setConfirm] = useState<{ kind: 'reverse' | 'refund' | 'deliver' | 'refund-surcharge'; amount?: string; reason?: string; includeSurcharge?: boolean } | null>(null);
  // «دقیقاً چه اتفاقی می‌افتد» — پیش‌نمایش سرور برای reverse/refund (با تغییر مبلغ/سوئیچ کارمزد دوباره محاسبه می‌شود)
  const [confirmPreview, setConfirmPreview] = useState<RefundPreviewData | null>(null);
  const [confirmPreviewLoading, setConfirmPreviewLoading] = useState(false);
  const [confirmPreviewError, setConfirmPreviewError] = useState<string | null>(null);
  const confirmIncludeFee = confirm?.includeSurcharge ?? (overview?.settings?.refundIncludesSurcharge ?? true);
  useEffect(() => {
    if (!confirm || !detailId || (confirm.kind !== 'reverse' && confirm.kind !== 'refund')) { setConfirmPreview(null); setConfirmPreviewError(null); return; }
    let cancelled = false;
    const amountNum = confirm.kind === 'refund' && confirm.amount && confirm.amount.trim() !== '' ? Number(confirm.amount.replace(/[^\d]/g, '')) : undefined;
    const t = setTimeout(async () => {
      setConfirmPreviewLoading(true); setConfirmPreviewError(null);
      try {
        const p = await adminRefundPreviewService.order(detailId, { amount: amountNum && amountNum > 0 ? amountNum : undefined, includeSurcharge: confirmIncludeFee });
        if (!cancelled) setConfirmPreview(p);
      } catch (e: any) {
        if (!cancelled) setConfirmPreviewError(e?.response?.data?.message || e?.message || 'خطا');
      } finally {
        if (!cancelled) setConfirmPreviewLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [confirm, detailId, confirmIncludeFee]);
  const runAction = async (kind: 'reverse' | 'refund' | 'deliver' | 'refund-surcharge' | 'inquiry', extra?: { amount?: number; reason?: string; inquiryId?: string; includeSurcharge?: boolean }) => {
    if (!detailId) return;
    setBusy(kind);
    try {
      let msg = 'انجام شد';
      if (kind === 'inquiry') {
        const r = await digipayAdminService.refundStatus(detailId, String(extra?.inquiryId || ''));
        msg = r.success
          ? `وضعیت ریفاند: ${DP_REFUND_STATUS_LABELS[r.refundStatus ?? 2]}${r.destinationType !== undefined ? ` — مقصد: ${DP_DESTINATION_LABELS[r.destinationType] || r.destinationType}` : ''}`
          : `استعلام ناموفق: ${r.message}`;
        enqueueSnackbar(msg, { variant: r.success ? 'info' : 'warning' });
      } else {
        const r: any = kind === 'reverse' ? await digipayAdminService.orderReverse(detailId, extra?.reason, extra?.includeSurcharge)
          : kind === 'refund' ? await digipayAdminService.orderRefund(detailId, extra?.amount, extra?.reason, extra?.includeSurcharge)
          : kind === 'deliver' ? await digipayAdminService.orderDeliver(detailId)
          : await digipayAdminService.orderRefundSurcharge(detailId, extra?.reason);
        msg = r?.message || (r?.data && r.data.message) || 'انجام شد';
        enqueueSnackbar(msg, { variant: 'success' });
      }
      await loadDetail(detailId);
      loadOrders();
      loadOverview();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در ارتباط با دیجی‌پی', { variant: 'error' });
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  // ── refunds ──
  const [refundList, setRefundList] = useState<DigipayRefundList | null>(null);
  const [refundsLoading, setRefundsLoading] = useState(false);
  const [refundPage, setRefundPage] = useState(0);
  const [refundRows, setRefundRows] = useState(25);
  const [refundStatusFilter, setRefundStatusFilter] = useState<string>('');
  const loadRefunds = useCallback(async () => {
    setRefundsLoading(true);
    try {
      setRefundList(await digipayAdminService.listRefunds({ page: refundPage + 1, limit: refundRows, status: refundStatusFilter }));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت بازگشت‌های وجه', { variant: 'error' });
    } finally {
      setRefundsLoading(false);
    }
  }, [refundPage, refundRows, refundStatusFilter, enqueueSnackbar]);
  useEffect(() => { if (tab === 2) loadRefunds(); }, [tab, loadRefunds]);

  // ── logs ──
  const [logs, setLogs] = useState<DigipayLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logPage, setLogPage] = useState(0);
  const [logRows, setLogRows] = useState(25);
  const [logAction, setLogAction] = useState('');
  const [logStatus, setLogStatus] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logSearchInput, setLogSearchInput] = useState('');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const r = await digipayAdminService.listLogs({ page: logPage + 1, limit: logRows, action: logAction, status: logStatus, search: logSearch });
      setLogs(r?.docs || []);
      setLogsTotal(r?.total || 0);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت لاگ‌ها', { variant: 'error' });
    } finally {
      setLogsLoading(false);
    }
  }, [logPage, logRows, logAction, logStatus, logSearch, enqueueSnackbar]);
  useEffect(() => { if (tab === 3) loadLogs(); }, [tab, loadLogs]);

  // ── tools ──
  const [connTest, setConnTest] = useState<DigipayConnectionTest | null>(null);
  const [connTesting, setConnTesting] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState<string>('1000000');
  const [quoteInstrument, setQuoteInstrument] = useState<DigipayInstrument>('credit');
  const [quote, setQuote] = useState<DigipayQuote | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [toolBusy, setToolBusy] = useState<string | null>(null);
  // 🧪 تیکت آزمایشی روی لایو (چون دیجی‌پی محیط تست ندارد)
  const [probeCell, setProbeCell] = useState('');
  const [probeInstrument, setProbeInstrument] = useState<DigipayInstrument>('ipg');
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState<DigipayProbeTicket | null>(null);

  const runConnTest = async () => {
    setConnTesting(true); setConnTest(null);
    try { setConnTest(await digipayAdminService.testConnection()); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || e?.message || 'اجرای تست ناموفق بود', { variant: 'error' }); }
    finally { setConnTesting(false); }
  };
  const runQuote = async () => {
    const amt = Number(String(quoteAmount).replace(/[^\d]/g, ''));
    if (!(amt > 0)) { enqueueSnackbar('مبلغ معتبر وارد کنید', { variant: 'warning' }); return; }
    setQuoting(true); setQuote(null);
    try { setQuote(await digipayAdminService.quote(amt, quoteInstrument)); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || e?.message || 'محاسبه ناموفق بود', { variant: 'error' }); }
    finally { setQuoting(false); }
  };
  const runProbe = async () => {
    setProbing(true); setProbe(null);
    try { setProbe(await digipayAdminService.probeTicket(probeCell, probeInstrument, 1000)); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || e?.message || 'ساخت تیکت آزمایشی ناموفق بود', { variant: 'error' }); }
    finally { setProbing(false); }
  };
  const runTool = async (kind: 'sweep-deliver' | 'refresh-refunds') => {
    setToolBusy(kind);
    try {
      const r: any = kind === 'sweep-deliver' ? await digipayAdminService.sweepDeliver(100) : await digipayAdminService.refreshRefunds(100);
      enqueueSnackbar(r?.message || 'انجام شد', { variant: 'success' });
      loadOverview();
      if (tab === 1) loadOrders();
      if (tab === 2) loadRefunds();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'اجرا ناموفق بود', { variant: 'error' });
    } finally {
      setToolBusy(null);
    }
  };

  // ---------------------------------------------------------------- columns

  const orderColumns: Column<DigipayOrderRow>[] = useMemo(() => [
    { id: 'orderNumber', label: 'سفارش', priority: 1, format: (v, row) => (
      <Box>
        <Typography variant="body2" fontWeight={700}><Mono>{v}</Mono></Typography>
        <Typography variant="caption" color="text.secondary">{row.user?.name || '—'}{row.user?.phone ? ` · ${fa(row.user.phone)}` : ''}</Typography>
      </Box>
    ) },
    { id: 'instrument', label: 'ابزار پرداخت', priority: 2, format: (v, row) => (
      <Stack spacing={0.5} alignItems="flex-start">
        <InstrumentChip instrument={v} />
        {row.isCredit && !row.deliverReportedAt && ['delivered', 'completed'].includes(row.status) && <Chip size="small" icon={<LocalShippingIcon sx={{ fontSize: 14 }} />} label="تحویل ثبت‌نشده" color="warning" variant="outlined" sx={{ fontSize: 10.5 }} />}
        {row.deliverError && <Tooltip title={row.deliverError}><Chip size="small" label="خطای ثبت تحویل" color="error" variant="outlined" sx={{ fontSize: 10.5 }} /></Tooltip>}
        {row.digipayReversedAt && <Chip size="small" label="reverse شده" color="secondary" variant="outlined" sx={{ fontSize: 10.5 }} />}
        {row.refundedViaDigipay > 0 && <Chip size="small" label={`برگشت ${fmtToman(row.refundedViaDigipay)}`} color="error" variant="outlined" sx={{ fontSize: 10.5 }} />}
      </Stack>
    ) },
    { id: 'trackingCode', label: 'کد پیگیری دیجی‌پی', priority: 3, format: (v, row) => (
      <Box>
        {v ? <Mono copy={v}>{v}</Mono> : '—'}
        {row.pspName && <Typography variant="caption" display="block" color="text.secondary">{row.pspName}{row.maskedPan ? ` · ${row.maskedPan}` : ''}</Typography>}
      </Box>
    ) },
    { id: 'paidAmount', label: 'مبلغ پرداختی', priority: 4, align: 'left', format: (v, row) => (
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="body2" fontWeight={700}>{fmtToman(v ?? row.payableAmount ?? row.totalAmount)}</Typography>
        {row.surcharge && row.surcharge.amount > 0 && (
          <Typography variant="caption" color={row.surcharge.status === 'charged' ? 'warning.dark' : 'text.secondary'}>
            کارمزد {fmtToman(row.surcharge.amount)} ({fa(row.surcharge.ratePercent)}٪){row.surcharge.status === 'adjusted' ? ' — تعدیل‌شده' : row.surcharge.status === 'refunded' ? ' — برگشت‌داده‌شده' : ''}
          </Typography>
        )}
      </Box>
    ) },
    { id: 'paidAt', label: 'زمان پرداخت', hideOnMobile: true, format: (v, row) => v ? formatDate(v) : formatDate(row.createdAt) },
    { id: 'paymentStatus', label: 'وضعیت سفارش', hideOnMobile: true, detail: true, format: (v, row) => `${row.status} / ${v}` },
  ], []);

  const refundColumns: Column<RefundRow>[] = useMemo(() => [
    { id: 'orderNumber', label: 'سفارش', priority: 1, format: (v: any, row: RefundRow) => <Box><Typography variant="body2" fontWeight={700}><Mono>{v}</Mono></Typography><Typography variant="caption" color="text.secondary">{DP_INSTRUMENT_LABELS[row.instrument || ''] || row.instrument || ''}</Typography></Box> },
    { id: 'refundAmount', label: 'ریفاند', priority: 2, format: (_v: any, row: RefundRow) => <Box><Typography variant="body2" fontWeight={700}>{fmtToman(row.refund?.amount)}</Typography><Typography variant="caption" color="text.secondary">{row.refund?.reason || ''}</Typography></Box> },
    { id: 'refundStatus', label: 'وضعیت', priority: 3, format: (_v: any, row: RefundRow) => <Stack spacing={0.3}><RefundStatusChip status={Number(row.refund?.status ?? 2)} />{row.refund?.destinationType !== undefined && row.refund?.destinationType !== null && <Typography variant="caption" color="text.secondary">{DP_DESTINATION_LABELS[row.refund.destinationType] || row.refund.destinationType}{row.refund?.destination ? ` · ${row.refund.destination}` : ''}</Typography>}</Stack> },
    { id: 'refundTracking', label: 'کد پیگیری ریفاند', priority: 4, format: (_v: any, row: RefundRow) => row.refund?.trackingCode ? <Mono copy={row.refund.trackingCode}>{row.refund.trackingCode}</Mono> : <Mono copy={row.refund?.providerId}>{row.refund?.providerId}</Mono> },
    { id: 'refundTime', label: 'زمان', hideOnMobile: true, format: (_v: any, row: RefundRow) => <Box><Typography variant="body2">{row.refund?.createdAt ? formatDate(row.refund.createdAt) : '—'}</Typography>{row.refund?.transferDate && <Typography variant="caption" color="text.secondary">انتقال: {formatDate(row.refund.transferDate)}</Typography>}</Box> },
  ], []);

  const logColumns: Column<DigipayLog>[] = useMemo(() => [
    { id: 'createdAt', label: 'زمان', priority: 1, format: (v) => formatDate(v) },
    { id: 'action', label: 'عملیات', priority: 2, format: (v, row) => (
      <Box>
        <Typography variant="body2" fontWeight={700}>{LOG_ACTION_LABELS[v] || v}</Typography>
        <Typography variant="caption" color="text.secondary">{row.message || ''}</Typography>
      </Box>
    ) },
    { id: 'status', label: 'نتیجه', priority: 3, format: (v, row) => (
      <Stack spacing={0.3}>
        <Chip size="small" label={v === 'success' ? 'موفق' : v === 'failed' ? 'ناموفق' : 'در انتظار'} sx={{ bgcolor: alpha(LOG_STATUS_COLORS[v] || '#64748B', 0.12), color: LOG_STATUS_COLORS[v] || '#64748B', fontWeight: 700, fontSize: 11 }} />
        {row.code !== undefined && row.code !== null && <Typography variant="caption" color="text.secondary">کد {fa(row.code)}</Typography>}
      </Stack>
    ) },
    { id: 'refId', label: 'کد پیگیری', priority: 4, format: (v, row) => (
      <Box>
        {v ? <Mono copy={v}>{v}</Mono> : '—'}
        {row.orderId && typeof row.orderId === 'object' && <Typography variant="caption" display="block" color="text.secondary">سفارش <Mono>{row.orderId.orderNumber}</Mono></Typography>}
      </Box>
    ) },
    { id: 'amount', label: 'مبلغ', align: 'left', hideOnMobile: true, format: (v) => fmtToman(v) },
    { id: 'error', label: 'خطا', detail: true, format: (v) => v ? <Typography variant="caption" color="error.main" sx={{ wordBreak: 'break-word' }}>{v}</Typography> : '—' },
  ], []);

  // ---------------------------------------------------------------- render helpers

  const counts = overview?.counts || {};
  const instrumentCount = (s: string) => counts[s]?.count || 0;
  const totalSurcharge = Object.values(counts).reduce((s, c) => s + (c.surcharge || 0), 0);

  const renderOverview = () => (
    <Box>
      {overview && !overview.configured && (
        <Alert severity="error" sx={{ mb: 2 }}>اعتبارنامه‌های دیجی‌پی در env سرور کامل نیست (DIGIPAY_USERNAME / PASSWORD / CLIENT_ID / CLIENT_SECRET). تا تکمیل آن، درگاه به کاربران نمایش داده نمی‌شود.</Alert>
      )}
      {overview && overview.configured && overview.clientIdHasNonAscii && (
        <Alert severity="warning" sx={{ mb: 2 }}>client_id در env شامل کاراکتر غیرانگلیسی (احتمالاً رقم فارسی) است؛ لاگین با آن 401 می‌گیرد. مقدار را از پنل دیجی‌پی کپی کنید.</Alert>
      )}
      {overview && overview.configured && !overview.settings.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>درگاه دیجی‌پی از تنظیمات <b>خاموش</b> است؛ گزینه‌های دیجی‌پی در سبد/چک‌اوت نمایش داده نمی‌شوند. از تب «تنظیمات و کارمزد» روشن کنید.</Alert>
      )}
      {overview && overview.pendingDeliver > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>{fa(overview.pendingDeliver)} خرید اعتباری تحویل‌شده هنوز در دیجی‌پی «تحویل» ثبت نشده (کرون هر ۱۵ دقیقه تلاش می‌کند). <Button size="small" onClick={() => { setFilter('pending_deliver'); setTab(1); }}>نمایش</Button></Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}><StatsCard title="کل پرداخت‌های دیجی‌پی" value={fa(overview?.total || 0)} subtitle={`کارمزد جمع: ${fmtToman(totalSurcharge)}`} icon={<ReceiptLongIcon />} iconBgColor={DP_BLUE} loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="کارت بانکی (IPG)" value={fa(instrumentCount('ipg'))} subtitle={fmtToman(counts.ipg?.amount || 0)} icon={<PaymentIcon />} iconBgColor="#2563EB" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="خرید اقساطی / اعتباری" value={fa(instrumentCount('credit'))} subtitle={fmtToman(counts.credit?.amount || 0)} icon={<CreditScoreIcon />} iconBgColor="#9333EA" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="کیف پول دیجی‌پی" value={fa(instrumentCount('wallet'))} subtitle={fmtToman(counts.wallet?.amount || 0)} icon={<AccountBalanceWalletIcon />} iconBgColor="#0EA5E9" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="تحویل ثبت‌نشده (اعتباری)" value={fa(overview?.pendingDeliver || 0)} subtitle={overview?.deliverErrors ? `${fa(overview.deliverErrors)} با خطا` : 'بدون خطا'} icon={<LocalShippingIcon />} iconBgColor="#D97706" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="بازگشت وجه" value={fa(Object.values(overview?.refunds || {}).reduce((s, c) => s + c.count, 0))} subtitle={fmtToman(Object.values(overview?.refunds || {}).reduce((s, c) => s + c.amount, 0))} icon={<ReplayIcon />} iconBgColor="#DC2626" loading={overviewLoading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>پیکربندی فعلی</Typography>
            <Row label="محیط" value={overview ? <Chip size="small" label={overview.environment === 'staging' ? 'تست (uat)' : 'لایو'} color={overview.environment === 'staging' ? 'warning' : 'success'} /> : '—'} />
            <Row label="آدرس API" value={<Mono copy={overview?.apiBase}>{overview?.apiBase || '—'}</Mono>} />
            <Row label="آدرس کال‌بک (POST)" value={<Mono copy={overview?.callbackUrl}>{overview?.callbackUrl || '—'}</Mono>} />
            <Row label="واحد مبلغ API / نسخه" value={overview ? `${overview.amountUnit === 'rial' ? 'ریال (تومان ×۱۰)' : 'تومان'} · Digipay-Version ${overview.version}` : '—'} />
            <Row label="اعتبارنامه" value={overview?.configured ? <Chip size="small" color="success" label="کامل" /> : <Chip size="small" color="error" label="ناقص" />} />
            <Row label="فعال برای کاربران" value={overview?.settings.enabled ? <Chip size="small" color="success" label="روشن" /> : <Chip size="small" label="خاموش" />} />
            <Row label="ابزارهای فعال" value={overview ? ['کارت بانکی', overview.settings.allowCredit ? 'اقساطی' : null, overview.settings.allowWallet ? 'کیف پول دیجی‌پی' : null].filter(Boolean).join(' · ') : '—'} />
            <Row label="کارمزد خرید اقساطی" value={overview ? `${fa(overview.settings.creditSurchargePercent)}٪ (${overview.settings.surchargeMode === 'gross_up' ? 'جبرانی' : 'روی مبلغ سفارش'})` : '—'} />
            <Row label="کارمزد کارت بانکی" value={overview ? `${fa(overview.settings.ipgSurchargePercent)}٪` : '—'} />
            <Row label="درگاه ترجیحی اقساطی" value={overview ? (overview.settings.creditPreferredGateway ? `کد ${fa(overview.settings.creditPreferredGateway)}` : 'صفحهٔ انتخاب ابزار دیجی‌پی') : '—'} />
            <Row label="سبد اعتباری (category / productType)" value={overview ? `${overview.settings.basketCategoryId} / ${fa(overview.settings.basketProductType)}` : '—'} />
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" variant="outlined" onClick={() => navigate('/settings?tab=digipay')}>ویرایش تنظیمات</Button>
              <Button size="small" variant="outlined" onClick={() => setTab(4)}>تست اتصال</Button>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>به تفکیک ابزار پرداخت</Typography>
            {(overview?.instruments || ['ipg', 'credit', 'wallet']).concat(counts.unknown ? ['unknown'] : []).map((s) => (
              <Box key={s} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6, borderBottom: '1px dashed', borderColor: 'divider' }}>
                <InstrumentChip instrument={s} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={700}>{fa(instrumentCount(s))} سفارش</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtToman(counts[s]?.amount || 0)}{counts[s]?.surcharge ? ` · کارمزد ${fmtToman(counts[s].surcharge)}` : ''}</Typography>
                </Box>
              </Box>
            ))}
            <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 2, mb: 1 }}>کارمزد اقساطی خریداران</Typography>
            {Object.entries(overview?.surchargeByStatus || {}).length === 0 && <Typography variant="body2" color="text.secondary">هنوز کارمزدی ثبت نشده</Typography>}
            {Object.entries(overview?.surchargeByStatus || {}).map(([st, c]) => (
              <Box key={st} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.4, fontSize: 13 }}>
                <span>{({ charged: 'دریافت‌شده', adjusted: 'تعدیل‌شده (به کیف پول)', refunded: 'برگشت‌داده‌شده', planned: 'در انتظار پرداخت' } as any)[st] || st}</span>
                <b>{fa(c.count)} · {fmtToman(c.amount)}</b>
              </Box>
            ))}
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>فعالیت ۲۴ ساعت اخیر (لاگ‌ها) — {fa(overview?.paidLast24h || 0)} پرداخت موفق</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(overview?.attempts24h || {}).length === 0 && <Typography variant="body2" color="text.secondary">فعالیتی ثبت نشده است</Typography>}
              {Object.entries(overview?.attempts24h || {}).map(([k, n]) => {
                const [action, status] = k.split(':');
                return <Chip key={k} size="small" label={`${LOG_ACTION_LABELS[action] || action} · ${status === 'success' ? 'موفق' : status === 'failed' ? 'ناموفق' : 'در انتظار'}: ${fa(n)}`} sx={{ bgcolor: alpha(LOG_STATUS_COLORS[status] || '#64748B', 0.1), color: LOG_STATUS_COLORS[status] || '#64748B', fontWeight: 700 }} />;
              })}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} disabled={!!toolBusy} onClick={() => runTool('sweep-deliver')}>{toolBusy === 'sweep-deliver' ? 'در حال اجرا…' : 'ثبت تحویل خریدهای اعتباری مانده'}</Button>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} disabled={!!toolBusy} onClick={() => runTool('refresh-refunds')}>{toolBusy === 'refresh-refunds' ? 'در حال استعلام…' : 'استعلام ریفاندهای نامشخص'}</Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderOrders = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="کد پیگیری دیجی‌پی، providerId، RRN یا شمارهٔ سفارش…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setSearch(searchInput.trim()); setSearchParams(searchInput.trim() ? { tab: '1', search: searchInput.trim() } : { tab: '1' }); } }}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5 }} /> }}
          />
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>ابزار پرداخت</InputLabel>
            <Select label="ابزار پرداخت" value={instrumentFilter} onChange={(e) => { setPage(0); setInstrumentFilter(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              {['ipg', 'credit', 'wallet'].map((s) => <MenuItem key={s} value={s}>{DP_INSTRUMENT_LABELS[s]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 230 }}>
            <InputLabel>فیلتر</InputLabel>
            <Select label="فیلتر" value={filter} onChange={(e) => { setPage(0); setFilter(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="paid">پرداخت‌شده</MenuItem>
              <MenuItem value="pending_deliver">اعتباری — تحویل ثبت‌نشده</MenuItem>
              <MenuItem value="deliver_error">خطای ثبت تحویل</MenuItem>
              <MenuItem value="reverse_window">در مهلت reverse (۲۵ دقیقه)</MenuItem>
              <MenuItem value="refunded">دارای بازگشت وجه</MenuItem>
              <MenuItem value="surcharge">دارای کارمزد اقساطی</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={() => { setPage(0); setSearch(searchInput.trim()); }}>جست‌وجو</Button>
          <IconButton size="small" onClick={loadOrders}><RefreshIcon /></IconButton>
        </Stack>
      </Paper>
      <ResponsiveTable<DigipayOrderRow>
        columns={orderColumns}
        data={orders}
        loading={ordersLoading}
        keyField="_id"
        emptyMessage="سفارشی با دیجی‌پی یافت نشد"
        pagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={ordersTotal}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        onRowClick={(row) => setDetailId(row._id)}
        renderActions={(row) => (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setDetailId(row._id); }}>مدیریت</Button>
            <Tooltip title="باز کردن در بخش سفارش‌ها"><IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/orders?search=${encodeURIComponent(row.orderNumber)}`); }}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>
        )}
      />
    </Box>
  );

  const renderDetailDialog = () => {
    const o = detail?.order;
    const cap = detail?.capabilities;
    return (
      <Dialog open={!!detailId} onClose={() => (busy ? undefined : setDetailId(null))} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DpLogo size={30} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>مدیریت سفارش دیجی‌پی {o ? <Mono>{o.orderNumber}</Mono> : ''}</Typography>
            {o && <Typography variant="caption" color="text.secondary">{o.user?.name || '—'}{o.user?.phone ? ` · ${fa(o.user.phone)}` : ''} · ثبت {formatDate(o.createdAt)}</Typography>}
          </Box>
          {o && (detail?.attemptOnly ? <Chip size="medium" label="تلاش نهایی‌نشده" color="warning" variant="outlined" /> : <InstrumentChip instrument={o.instrument} size="medium" />)}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail || !o ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2}>
              {detail.attemptOnly && (
                <Grid item xs={12}>
                  <Alert severity="warning">{detail.attemptNote || 'پرداخت این سفارش با دیجی‌پی نهایی نشده است.'} فقط لاگ‌ها در دسترس است؛ عملیات مالی برای این سفارش معنا ندارد.</Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>تراکنش</Typography>
                  <Row label="کد پیگیری دیجی‌پی (trackingCode)" value={o.trackingCode ? <Mono copy={o.trackingCode}>{o.trackingCode}</Mono> : '—'} />
                  <Row label="شناسهٔ خرید ما (providerId)" value={o.providerId ? <Mono copy={o.providerId}>{o.providerId}</Mono> : '—'} />
                  <Row label="نوع تیکت" value={o.ticketType !== null && o.ticketType !== undefined ? (DP_TICKET_TYPE_LABELS[o.ticketType] || fa(o.ticketType)) : '—'} />
                  {o.pspName && <Row label="PSP / ترمینال" value={`${o.pspName}${o.pspCode ? ` (${o.pspCode})` : ''}${o.terminalId ? ` · ${o.terminalId}` : ''}`} />}
                  {o.rrn && <Row label="RRN" value={<Mono copy={o.rrn}>{o.rrn}</Mono>} />}
                  {o.maskedPan && <Row label="کارت" value={<Mono>{o.maskedPan}</Mono>} />}
                  <Row label="مبلغ سفارش (کالا)" value={fmtToman(o.totalAmount)} />
                  <Row label="کارمزد" value={o.surcharge && o.surcharge.amount > 0 ? `${fmtToman(o.surcharge.amount)} (${fa(o.surcharge.ratePercent)}٪ ${o.surcharge.mode === 'gross_up' ? 'جبرانی' : 'روی مبلغ'}) — ${({ charged: 'دریافت‌شده', adjusted: 'تعدیل‌شده', refunded: 'برگشت‌داده‌شده', planned: 'در انتظار' } as any)[o.surcharge.status] || o.surcharge.status}` : 'بدون کارمزد'} />
                  {o.surcharge?.actualInstrument && o.surcharge.actualInstrument !== o.surcharge.instrument && <Row label="ابزار واقعی پرداخت" value={`${DP_INSTRUMENT_LABELS[o.surcharge.actualInstrument] || o.surcharge.actualInstrument} (انتخاب خریدار: ${DP_INSTRUMENT_LABELS[o.surcharge.instrument || ''] || o.surcharge.instrument})`} />}
                  <Row label="مبلغ پرداخت‌شده" value={<b>{fmtToman(o.paidAmount ?? o.payableAmount ?? o.totalAmount)}</b>} />
                  <Row label="زمان تأیید (verify)" value={o.verifiedAt ? formatDate(o.verifiedAt) : '—'} />
                  <Row label="مهلت reverse" value={o.digipayReversedAt ? `reverse شد (${formatDate(o.digipayReversedAt)})` : o.reverseDeadline ? (o.reverseWindowOpen ? `تا ${formatDate(o.reverseDeadline)}` : 'گذشته') : '—'} />
                  {o.isCredit && <Row label="ثبت تحویل در دیجی‌پی" value={o.deliverReportedAt ? formatDate(o.deliverReportedAt) : o.deliverError ? <span style={{ color: '#DC2626' }}>خطا: {o.deliverError}</span> : 'ثبت نشده'} />}
                  <Row label="برگشت‌داده‌شده از دیجی‌پی" value={o.refundedViaDigipay > 0 ? fmtToman(o.refundedViaDigipay) : '—'} />
                  <Row label="وضعیت سفارش در کازماشاپ" value={`${o.status} / ${o.paymentStatus}`} />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>عملیات دیجی‌پی</Typography>
                  <Stack spacing={1}>
                    {cap?.canReverse && <Button variant="outlined" size="small" color="secondary" disabled={!!busy} onClick={() => setConfirm({ kind: 'reverse' })}>{busy === 'reverse' ? 'در حال بازگشت…' : 'بازگشت فوری کل مبلغ (reverse — تا ۲۵ دقیقه)'}</Button>}
                    {cap?.canRefund && <Button variant="outlined" size="small" color="error" disabled={!!busy} onClick={() => setConfirm({ kind: 'refund', amount: '' })}>بازگشت وجه از دیجی‌پی (refund کامل/جزئی)</Button>}
                    {cap?.canDeliver && <Button variant="contained" size="small" color="success" disabled={!!busy} onClick={() => setConfirm({ kind: 'deliver' })}>{busy === 'deliver' ? 'در حال ثبت…' : 'ثبت تحویل خرید اعتباری (deliver)'}</Button>}
                    {cap?.canRefundSurcharge && <Button variant="text" size="small" color="warning" disabled={!!busy} onClick={() => setConfirm({ kind: 'refund-surcharge' })}>بازگشت کارمزد اقساطی به کیف پول خریدار</Button>}
                    {!cap?.canReverse && !cap?.canRefund && !cap?.canDeliver && !cap?.canRefundSurcharge && <Typography variant="body2" color="text.secondary">عملیاتی برای این سفارش در دسترس نیست</Typography>}
                  </Stack>
                  <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mt: 1.5, fontSize: 11.5, lineHeight: 1.9 }}>
                    reverse فقط برای پرداخت کارتی و تا ۲۵ دقیقه بعد از verify ممکن است و کل مبلغ را برمی‌گرداند. refund از طریق API دیجی‌پی به کارت/کیف پول/اعتبار خریدار می‌رود (می‌تواند جزئی باشد) و وضعیت نهایی‌اش با استعلام مشخص می‌شود. برای هر خرید فقط یکی از reverse یا refund مجاز است. برای سفارش دیجی‌پی، استرداد داخلی به کیف پول کازماشاپ انجام ندهید.
                  </Alert>
                </Paper>
                {o.refunds.length > 0 && (
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>بازگشت‌های وجه ({fa(o.refunds.length)})</Typography>
                    <Stack spacing={0.75}>
                      {o.refunds.map((r) => (
                        <Box key={r.providerId} sx={{ p: 1, borderRadius: 1, border: '1px dashed', borderColor: 'divider', fontSize: 12.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <b>{fmtToman(r.amount)}</b>
                            <RefundStatusChip status={Number(r.status)} />
                            <Typography variant="caption" color="text.secondary">{formatDate(r.createdAt)}</Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button size="small" disabled={!!busy} onClick={() => runAction('inquiry', { inquiryId: r.trackingCode || r.providerId })}>{busy === 'inquiry' ? '…' : 'استعلام'}</Button>
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>کد ریفاند: <Mono copy={r.trackingCode || r.providerId}>{r.trackingCode || r.providerId}</Mono>{r.destinationType !== undefined && r.destinationType !== null ? ` · مقصد: ${DP_DESTINATION_LABELS[r.destinationType] || r.destinationType}${r.destination ? ` (${r.destination})` : ''}` : ''}{r.transferDate ? ` · انتقال: ${formatDate(r.transferDate)}` : ''}{r.reason ? ` · ${r.reason}` : ''}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                )}
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>آیتم‌های سفارش ({fa(detail.items.length)}) — جمع آیتم‌های فعال: {fmtToman(detail.activeItemsSum)}</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead><tr style={{ textAlign: 'right', color: '#64748B' }}><th style={{ padding: 6 }}>کالا</th><th style={{ padding: 6 }}>تعداد</th><th style={{ padding: 6 }}>قیمت واحد</th><th style={{ padding: 6 }}>جمع</th><th style={{ padding: 6 }}>وضعیت</th><th style={{ padding: 6 }}>مسترد شده</th></tr></thead>
                      <tbody>
                        {detail.items.map((it) => {
                          const inactive = ['cancelled', 'refunded'].includes(it.status);
                          return (
                            <tr key={it._id} style={{ borderTop: '1px solid #eee', opacity: inactive ? 0.55 : 1, textDecoration: inactive ? 'line-through' : 'none' }}>
                              <td style={{ padding: 6 }}>{it.productTitle}{it.storeName ? <span style={{ color: '#64748B' }}> · {it.storeName}</span> : ''}</td>
                              <td style={{ padding: 6 }}>{fa(it.quantity)}</td>
                              <td style={{ padding: 6 }}>{fmtToman(it.unitPrice)}</td>
                              <td style={{ padding: 6 }}>{fmtToman(it.totalPrice)}</td>
                              <td style={{ padding: 6 }}>{it.status}</td>
                              <td style={{ padding: 6 }}>{it.refundedAmount ? fmtToman(it.refundedAmount) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>تایم‌لاین دیجی‌پی ({fa(detail.logs.length)})</Typography>
                  {detail.logs.length === 0 ? <Typography variant="body2" color="text.secondary">لاگی ثبت نشده</Typography> : (
                    <Stack spacing={0.75}>
                      {detail.logs.map((l) => (
                        <Box key={l._id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1, borderRadius: 1, bgcolor: alpha(LOG_STATUS_COLORS[l.status] || '#64748B', 0.06) }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.8, bgcolor: LOG_STATUS_COLORS[l.status] || '#64748B', flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700}>{LOG_ACTION_LABELS[l.action] || l.action} — {l.message || (l.status === 'success' ? 'موفق' : l.status)}{l.code !== undefined && l.code !== null ? ` (کد ${fa(l.code)})` : ''}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDate(l.createdAt)}{l.amount ? ` · ${fmtToman(l.amount)}` : ''}{l.meta?.instrument ? ` · ${DP_INSTRUMENT_LABELS[l.meta.instrument] || l.meta.instrument}` : ''}{l.meta?.kind ? ` · ${l.meta.kind}` : ''}</Typography>
                            {l.error && <Typography variant="caption" color="error.main" sx={{ display: 'block', wordBreak: 'break-word' }}>{l.error}</Typography>}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {o && <Button startIcon={<OpenInNewIcon />} onClick={() => navigate(`/orders?search=${encodeURIComponent(o.orderNumber)}`)}>باز کردن در سفارش‌ها</Button>}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDetailId(null)} disabled={!!busy}>بستن</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirm || !detail) return null;
    const o = detail.order;
    const paid = Math.round(Number(o.paidAmount ?? o.payableAmount ?? o.totalAmount) || 0);
    const maxRefund = detail.maxRefundable ?? paid;
    const titles: Record<string, string> = { reverse: 'بازگشت فوری کل مبلغ (reverse)', refund: 'بازگشت وجه از دیجی‌پی (refund)', deliver: 'ثبت تحویل خرید اعتباری در دیجی‌پی', 'refund-surcharge': 'بازگشت کارمزد اقساطی به کیف پول خریدار' };
    const amountNum = confirm.amount && confirm.amount.trim() !== '' ? Number(confirm.amount.replace(/[^\d]/g, '')) : undefined;
    // کارمزد اقساطیِ هنوز نزد ما (charged) ⇒ سوئیچ «کارمزد هم برگردد» با پیش‌فرض تنظیم دیجی‌پی
    const feeOutstanding = o.surcharge && o.surcharge.amount > 0 && o.surcharge.status === 'charged' ? Math.round(Number(o.surcharge.amount)) : 0;
    const includeFee = confirm.includeSurcharge ?? (overview?.settings?.refundIncludesSurcharge ?? true);
    const feeSwitch = feeOutstanding > 0 && (confirm.kind === 'reverse' || confirm.kind === 'refund') ? (
      <FormControlLabel
        sx={{ m: 0, mb: 1, alignItems: 'flex-start' }}
        control={<Switch size="small" checked={includeFee} onChange={(e) => setConfirm({ ...confirm, includeSurcharge: e.target.checked })} />}
        label={<Typography variant="body2">کارمزد اقساطی <b>{fmtToman(feeOutstanding)}</b> هم به خریدار برگردد{includeFee ? '' : ' (خاموش: فقط ارزش کالا برمی‌گردد و کارمزد نزد پلتفرم می‌ماند؛ خریدار مطلع می‌شود)'}</Typography>}
      />
    ) : null;
    return (
      <Dialog open onClose={() => (busy ? undefined : setConfirm(null))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{titles[confirm.kind]}</DialogTitle>
        <DialogContent dividers>
          {confirm.kind === 'reverse' && <Alert severity="error" sx={{ mb: 1 }}>کل مبلغ <b>{fmtToman(paid)}</b> (شامل کارمزد) به حساب خریدار برمی‌گردد و سفارش «مسترد» می‌شود. فقط تا ۲۵ دقیقه بعد از verify ممکن است و <b>برگشت‌ناپذیر</b> است. بعد از reverse دیگر refund ممکن نیست.{feeOutstanding > 0 && !includeFee ? ' چون «کارمزد هم برگردد» خاموش است، reverse رد می‌شود؛ از «بازگشت وجه (refund)» استفاده کنید یا سوئیچ را روشن کنید.' : ''}</Alert>}
          {confirm.kind === 'reverse' && feeSwitch}
          {confirm.kind === 'reverse' && <RefundPreviewSteps preview={confirmPreview} loading={confirmPreviewLoading} error={confirmPreviewError} />}
          {confirm.kind === 'refund' && (
            <Box>
              <Alert severity="warning" sx={{ mb: 1.5 }}>مبلغ از طریق API دیجی‌پی به خریدار برمی‌گردد (کارت/کیف پول/اعتبار). حداکثر قابل بازگشت: <b>{fmtToman(Math.max(0, maxRefund - (includeFee ? 0 : feeOutstanding)))}</b>{feeOutstanding > 0 && !includeFee ? ' (بدون کارمزد)' : ''}. خالی بگذارید تا کل باقی‌مانده برگردد. <b>برگشت‌ناپذیر است.</b></Alert>
              {feeSwitch}
              <RefundPreviewSteps preview={confirmPreview} loading={confirmPreviewLoading} error={confirmPreviewError} />
              <TextField fullWidth size="small" label="مبلغ بازگشت (تومان) — اختیاری" value={confirm.amount || ''} onChange={(e) => setConfirm({ ...confirm, amount: e.target.value })} placeholder={String(maxRefund)} inputProps={{ inputMode: 'numeric', dir: 'ltr' }} sx={{ mb: 1.5 }} />
              <TextField fullWidth size="small" label="دلیل (اختیاری)" value={confirm.reason || ''} onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })} />
              {amountNum !== undefined && amountNum > maxRefund && <Typography variant="caption" color="error.main">مبلغ نمی‌تواند بیشتر از {fmtToman(maxRefund)} باشد</Typography>}
            </Box>
          )}
          {confirm.kind === 'deliver' && <Alert severity="info" sx={{ mb: 1 }}>طبق مستند دیجی‌پی، خریدهای اعتباری/BNPL بعد از تحویل کالا باید با `purchases/deliver` گزارش شوند تا قرارداد اعتباری خریدار نهایی شود. شمارهٔ سفارش به‌عنوان شمارهٔ فاکتور و شناسهٔ آیتم‌های فعال به‌عنوان محصولات ارسال می‌شود.</Alert>}
          {confirm.kind === 'refund-surcharge' && <Alert severity="warning" sx={{ mb: 1 }}>کارمزد اقساطی <b>{fmtToman(o.surcharge?.amount || 0)}</b> به کیف پول داخلی خریدار (کازماشاپ) واریز می‌شود، نه از طریق دیجی‌پی. برای بازگشت خودِ مبلغ خرید از «بازگشت وجه از دیجی‌پی» استفاده کنید.</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={!!busy}>انصراف</Button>
          <Button
            variant="contained"
            color={confirm.kind === 'reverse' ? 'secondary' : confirm.kind === 'refund' ? 'error' : confirm.kind === 'deliver' ? 'success' : 'warning'}
            disabled={!!busy || (confirm.kind === 'refund' && amountNum !== undefined && (!(amountNum > 0) || amountNum > maxRefund))}
            onClick={() => runAction(confirm.kind, { amount: confirm.kind === 'refund' ? amountNum : undefined, reason: confirm.reason, includeSurcharge: feeOutstanding > 0 && (confirm.kind === 'reverse' || confirm.kind === 'refund') ? includeFee : undefined })}
          >
            {busy ? 'در حال انجام…' : confirm.kind === 'refund-surcharge' ? 'تأیید و واریز' : 'تأیید و ارسال به دیجی‌پی'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderRefunds = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>وضعیت ریفاند</InputLabel>
            <Select label="وضعیت ریفاند" value={refundStatusFilter} onChange={(e) => { setRefundPage(0); setRefundStatusFilter(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="0">موفق</MenuItem>
              <MenuItem value="1">ناموفق</MenuItem>
              <MenuItem value="2">نامشخص / در جریان</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} disabled={!!toolBusy} onClick={() => runTool('refresh-refunds')}>{toolBusy === 'refresh-refunds' ? 'در حال استعلام…' : 'استعلام دسته‌ای ریفاندهای نامشخص'}</Button>
          <IconButton size="small" onClick={loadRefunds}><RefreshIcon /></IconButton>
        </Stack>
      </Paper>
      <ResponsiveTable<RefundRow>
        columns={refundColumns}
        data={(refundList?.docs || []).map((r) => ({ ...r, _id: `${r.orderId}:${r.refund?.providerId || ''}` }))}
        loading={refundsLoading}
        keyField="_id"
        emptyMessage="بازگشت وجهی از طریق دیجی‌پی ثبت نشده است"
        pagination
        page={refundPage}
        rowsPerPage={refundRows}
        totalCount={refundList?.total || 0}
        onPageChange={setRefundPage}
        onRowsPerPageChange={(n) => { setRefundRows(n); setRefundPage(0); }}
        onRowClick={(row) => setDetailId(row.orderId)}
        renderActions={(row) => <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setDetailId(row.orderId); }}>سفارش</Button>}
      />
      {refundList && refundList.reversed.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>reverse شده‌ها (بازگشت فوری) — {fa(refundList.reversed.length)} مورد اخیر</Typography>
          <Stack spacing={0.5}>
            {refundList.reversed.map((r) => (
              <Box key={r.orderId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12.5, py: 0.5, borderBottom: '1px dashed', borderColor: 'divider', cursor: 'pointer' }} onClick={() => setDetailId(r.orderId)}>
                <span><Mono>{r.orderNumber}</Mono>{r.trackingCode ? <> · <Mono>{r.trackingCode}</Mono></> : null}</span>
                <span><b>{fmtToman(r.amount)}</b> · {formatDate(r.reversedAt)}</span>
              </Box>
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );

  const renderLogs = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField size="small" fullWidth placeholder="کد پیگیری، providerId، پیام یا متن خطا…" value={logSearchInput} onChange={(e) => setLogSearchInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setLogPage(0); setLogSearch(logSearchInput.trim()); } }} InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5 }} /> }} />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>عملیات</InputLabel>
            <Select label="عملیات" value={logAction} onChange={(e) => { setLogPage(0); setLogAction(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              {Object.entries(LOG_ACTION_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>نتیجه</InputLabel>
            <Select label="نتیجه" value={logStatus} onChange={(e) => { setLogPage(0); setLogStatus(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="success">موفق</MenuItem>
              <MenuItem value="failed">ناموفق</MenuItem>
              <MenuItem value="pending">در انتظار</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={() => { setLogPage(0); setLogSearch(logSearchInput.trim()); }}>جست‌وجو</Button>
          <IconButton size="small" onClick={loadLogs}><RefreshIcon /></IconButton>
        </Stack>
      </Paper>
      <ResponsiveTable<DigipayLog>
        columns={logColumns}
        data={logs}
        loading={logsLoading}
        keyField="_id"
        emptyMessage="لاگی ثبت نشده است"
        pagination
        page={logPage}
        rowsPerPage={logRows}
        totalCount={logsTotal}
        onPageChange={setLogPage}
        onRowsPerPageChange={(n) => { setLogRows(n); setLogPage(0); }}
        onRowClick={(row) => setExpandedLog(expandedLog === row._id ? null : row._id)}
        renderActions={(row) => (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={(e) => { e.stopPropagation(); setExpandedLog(expandedLog === row._id ? null : row._id); }}>{expandedLog === row._id ? 'بستن' : 'جزئیات'}</Button>
            {row.orderId && typeof row.orderId === 'object' && <Button size="small" onClick={(e) => { e.stopPropagation(); setDetailId(String((row.orderId as any)._id)); }}>سفارش</Button>}
          </Stack>
        )}
      />
      {(() => {
        const l = expandedLog ? logs.find((x) => x._id === expandedLog) : null;
        return (
          <Dialog open={!!l} onClose={() => setExpandedLog(null)} maxWidth="md" fullWidth>
            {l && (
              <>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DpLogo size={28} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={800}>جزئیات لاگ — {LOG_ACTION_LABELS[l.action] || l.action}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(l.createdAt)}{l.message ? ` · ${l.message}` : ''}</Typography>
                  </Box>
                  <Chip size="small" label={l.status === 'success' ? 'موفق' : l.status === 'failed' ? 'ناموفق' : 'در انتظار'} sx={{ bgcolor: alpha(LOG_STATUS_COLORS[l.status] || '#64748B', 0.12), color: LOG_STATUS_COLORS[l.status] || '#64748B', fontWeight: 700 }} />
                </DialogTitle>
                <DialogContent dividers>
                  <LogDetails log={l} amountUnit={amountUnit} />
                </DialogContent>
                <DialogActions>
                  {l.orderId && typeof l.orderId === 'object' && <Button startIcon={<OpenInNewIcon />} onClick={() => { setExpandedLog(null); setDetailId(String((l.orderId as any)._id)); }}>مدیریت سفارش</Button>}
                  <Box sx={{ flex: 1 }} />
                  <Button onClick={() => setExpandedLog(null)}>بستن</Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        );
      })()}
    </Box>
  );



  const renderTools = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>تست اتصال (DNS + احراز هویت OAuth)</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>هیچ تراکنشی ثبت نمی‌کند. لاگین با Basic(client_id:client_secret) و username/password — دقیقاً همان کاری که پلاگین رسمی دیجی‌پی می‌کند.</Typography>
          <Button variant="contained" size="small" disabled={connTesting} onClick={runConnTest}>{connTesting ? 'در حال تست…' : 'اجرای تست اتصال'}</Button>
          {connTest && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: alpha(connTest.login.ok ? '#16A34A' : '#DC2626', 0.08), fontSize: 13, lineHeight: 2 }}>
              <div><b>آدرس:</b> <Mono>{connTest.apiBase}</Mono> {connTest.amountUnit ? `(واحد: ${connTest.amountUnit})` : ''}</div>
              <div><b>پیکربندی:</b> {connTest.configured ? 'کامل' : 'ناقص'}{connTest.clientIdHasNonAscii ? ' — ⚠️ client_id کاراکتر غیرانگلیسی دارد' : ''}</div>
              <div><b>DNS:</b> {connTest.dns.ok ? `OK (${connTest.dns.addresses.join(', ')})` : `ناموفق ${connTest.dns.error || ''}`}</div>
              <div><b>لاگین:</b> {connTest.login.ok ? `موفق (${fa(connTest.login.ms)} ms)` : `ناموفق ${connTest.login.error || ''}`}</div>
              {connTest.credentials && connTest.credentials.length > 0 && (
                <div style={{ marginTop: 6 }}>
                  <b>اعتبارنامه‌ها (env سرور، ماسک‌شده):</b>
                  {connTest.credentials.map((c) => (
                    <div key={c.key} style={{ fontSize: 12 }}>
                      <Mono>{c.key}</Mono> = <Mono>{c.masked || '—'}</Mono> · طول مؤثر {fa(c.effectiveLength)}
                      {c.quotedInEnv && <span style={{ color: '#B45309' }}> · ⚠️ با کوتیشن ذخیره شده</span>}
                      {c.hasWhitespace && <span style={{ color: '#B45309' }}> · ⚠️ فاصله/خط جدید</span>}
                      {!c.present && <span style={{ color: '#DC2626' }}> · ❌ تعریف نشده</span>}
                    </div>
                  ))}
                </div>
              )}
              {connTest.envWarnings && connTest.envWarnings.length > 0 && connTest.envWarnings.map((w, i) => (
                <div key={i} style={{ color: '#B45309', fontWeight: 700 }}>⚠️ {w}</div>
              ))}
              <div style={{ marginTop: 6, fontWeight: 700 }}>{connTest.hint}</div>
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>تیکت آزمایشی روی سرور واقعی (بدون پرداخت)</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, lineHeight: 1.9 }}>
            دیجی‌پی محیط تست ندارد. این ابزار یک تیکت ۱٬۰۰۰ تومانی روی لایو می‌سازد و لینک صفحهٔ پرداخت را می‌دهد؛ سفارشی ساخته نمی‌شود و تا کسی پرداخت نکند پولی جابه‌جا نمی‌شود (تیکت خودش منقضی می‌شود). لینک را باز کنید و فقط ببینید مبلغ «۱٬۰۰۰ تومان» نشان داده می‌شود — این یعنی احراز هویت، هدرها، فیلدها و واحد مبلغ درست‌اند.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" label="موبایل خریدار (خالی = شمارهٔ خودتان)" value={probeCell} onChange={(e) => setProbeCell(e.target.value)} inputProps={{ dir: 'ltr', inputMode: 'tel' }} sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>ابزار</InputLabel>
              <Select label="ابزار" value={probeInstrument} onChange={(e) => setProbeInstrument(e.target.value as DigipayInstrument)}>
                <MenuItem value="ipg">کارت بانکی</MenuItem>
                <MenuItem value="credit">اقساطی (با سبد)</MenuItem>
                <MenuItem value="wallet">کیف پول</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" size="small" disabled={probing} onClick={runProbe}>{probing ? 'در حال ساخت…' : 'ساخت تیکت ۱٬۰۰۰ تومانی'}</Button>
          </Stack>
          {probe && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(probe.ok ? '#16A34A' : '#DC2626', 0.08), fontSize: 13, lineHeight: 2 }}>
              <div><b>آدرس:</b> <Mono>{probe.apiBase}</Mono> · <b>ابزار:</b> {probe.instrument} · <b>زمان:</b> {fa(probe.ms)} ms</div>
              <div><b>مبلغ ارسالی به API:</b> <Mono>{String(probe.amountSent)}</Mono> ({probe.amountUnit === 'rial' ? 'ریال' : 'تومان'}) برای {fmtToman(probe.amountToman)} · <b>providerId:</b> <Mono>{probe.providerId}</Mono></div>
              {probe.ok ? (
                <>
                  <div><b>تیکت:</b> <Mono>{probe.ticket}</Mono></div>
                  <div><b>لینک صفحهٔ پرداخت:</b> <a href={probe.redirectUrl} target="_blank" rel="noreferrer">باز کردن در تب جدید</a></div>
                  <div style={{ marginTop: 6, fontWeight: 700 }}>{probe.hint}</div>
                </>
              ) : (
                <div><b>خطا:</b> {probe.error} {probe.httpStatus ? `(http ${probe.httpStatus}${probe.resultStatus !== undefined ? ` · کد ${probe.resultStatus}` : ''})` : ''}</div>
              )}
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>محاسبهٔ کارمزد و مبلغ ارسالی به دیجی‌پی</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>همان چیزی که خریدار در چک‌اوت می‌بیند + مبلغی که عیناً به API می‌رود (با تبدیل واحد).</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" label="مبلغ سفارش (تومان)" value={quoteAmount} onChange={(e) => setQuoteAmount(e.target.value)} inputProps={{ dir: 'ltr', inputMode: 'numeric' }} sx={{ flex: 1 }} />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>ابزار</InputLabel>
              <Select label="ابزار" value={quoteInstrument} onChange={(e) => setQuoteInstrument(e.target.value as DigipayInstrument)}>
                <MenuItem value="ipg">کارت بانکی</MenuItem>
                <MenuItem value="credit">اقساطی</MenuItem>
                <MenuItem value="wallet">کیف پول</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" size="small" disabled={quoting} onClick={runQuote}>{quoting ? '…' : 'محاسبه'}</Button>
          </Stack>
          {quote && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(quote.available ? '#16A34A' : '#D97706', 0.08), fontSize: 13, lineHeight: 2 }}>
              <div><b>مبلغ سفارش:</b> {fmtToman(quote.amount)} · <b>کارمزد:</b> {fmtToman(quote.surchargeAmount)} ({fa(quote.ratePercent)}٪ {quote.mode === 'gross_up' ? 'جبرانی' : 'روی مبلغ'}) · <b>قابل پرداخت:</b> {fmtToman(quote.payableAmount)}</div>
              <div><b>مبلغ ارسالی به API:</b> <Mono>{String(quote.gatewayAmount)}</Mono> ({quote.amountUnit === 'rial' ? 'ریال' : 'تومان'})</div>
              <div><b>preferredGateway:</b> {quote.preferredGateway === null ? 'ندارد (صفحهٔ انتخاب دیجی‌پی)' : fa(quote.preferredGateway)} · <b>basketDetailsDto:</b> {quote.basketRequired ? 'الزامی' : 'ارسال نمی‌شود'}</div>
              <div><b>در دسترس برای خریدار:</b> {quote.available ? 'بله' : 'خیر (درگاه/ابزار خاموش یا اعتبارنامه ناقص)'}</div>
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>کارهای دسته‌ای</Typography>
          <Stack spacing={1}>
            <Button variant="contained" size="small" startIcon={<PlayArrowIcon />} disabled={!!toolBusy} onClick={() => runTool('sweep-deliver')}>{toolBusy === 'sweep-deliver' ? 'در حال اجرا…' : 'ثبت تحویل خریدهای اعتباری تحویل‌شده (اجرای دستی کرون)'}</Button>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} disabled={!!toolBusy} onClick={() => runTool('refresh-refunds')}>{toolBusy === 'refresh-refunds' ? 'در حال استعلام…' : 'استعلام دسته‌ای ریفاندهای نامشخص'}</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.8 }}>
            کرون «digipay-credit-deliver-report» هر ۱۵ دقیقه خودکار اجرا می‌شود؛ این دکمه‌ها همان کار را همین حالا انجام می‌دهند.
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>اطلاعات لازم برای پشتیبانی دیجی‌پی</Typography>
          <Row label="IP سرور" value={<Mono copy="176.65.241.166">176.65.241.166</Mono>} />
          <Row label="آدرس کال‌بک" value={<Mono copy={overview?.callbackUrl}>{overview?.callbackUrl || '—'}</Mono>} />
          <Row label="پنل پذیرندگان" value={<Mono copy="https://business.mydigipay.com">business.mydigipay.com</Mono>} />
          <Row label="پشتیبانی" value={<Mono copy="02153924000">021-53924000</Mono>} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.8 }}>
            اعتبارنامهٔ UPG (نام کاربری/رمز + client id/secret) در پنل پذیرندگان، بخش تنظیمات فنی ابزار «درگاه پرداخت یکپارچه» تعریف می‌شود. پلاگین رسمی دیجی‌پی فقط با آدرس لایو کار می‌کند (محیط تست عمومی ندارند).
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderGuide = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>چرخهٔ عمر تراکنش دیجی‌پی (مستند UPG)</Typography>
        <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12.5, direction: 'ltr', textAlign: 'left', overflowX: 'auto' }}>
{`oauth/token (Basic + username/password)  →  tickets/business?type=11 {amount, cellNumber, providerId, callbackUrl, additionalInfo.preferredGateway?, basketDetailsDto?}
   preferredGateway 2 = کارت بانکی مستقیم · 0 = کیف پول دیجی‌پی · بدون آن = صفحهٔ انتخاب ابزار (اقساطی هم آنجاست)
→ کاربر در درگاه → callback POST {result SUCCESS|FAILURE, providerId, trackingCode, amount, type, rrn?, psp?}
→ purchases/verify?type={type} {trackingCode, providerId}   (9011 ⇒ تکرار · بدون verify ⇒ پول خودکار برمی‌گردد)
→ خرید اعتباری (type 5/13/24): بعد از تحویل ⇒ purchases/deliver {trackingCode, invoiceNumber, products[], deliveryDate}
بازگشت وجه:  reverse (≤۲۵ دقیقه، فقط کارتی، کل مبلغ)   |   refunds?type= {providerId جدید, amount, saleTrackingCode} → refunds/{inquiryId} (0 موفق · 1 ناموفق · 2 نامشخص)
فقط یکی از reverse یا refund برای هر خرید مجاز است.`}
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 2 }}>
          • مبالغ API {amountUnit === 'rial' ? 'ریال هستند (سیستم تومان ×۱۰ می‌کند)' : 'تومان فرستاده می‌شوند'}. • providerId برای هر تلاش یکتا ساخته می‌شود (9008 = تکراری). • هدرهای Agent: WEB و Digipay-Version الزامی‌اند. • کارمزد اقساطی خریدار بیرون از مبلغ سفارش ثبت می‌شود و اگر خریدار در صفحهٔ دیجی‌پی به‌جای اعتباری با کارت پرداخت کند، خودکار به کیف پولش برمی‌گردد.
        </Typography>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>کدهای نتیجهٔ دیجی‌پی (result.status در پاسخ 422) و اقدام پیشنهادی</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr style={{ textAlign: 'right', color: '#64748B' }}><th style={{ padding: 8 }}>کد</th><th style={{ padding: 8 }}>پیام</th><th style={{ padding: 8 }}>اقدام</th></tr></thead>
            <tbody>
              {DP_RESULT_CODES.map((e) => (
                <tr key={e.code} style={{ borderTop: '1px solid #eee', verticalAlign: 'top' }}>
                  <td style={{ padding: 8, fontWeight: 700 }}>{fa(e.code)}</td>
                  <td style={{ padding: 8 }}>{e.message}</td>
                  <td style={{ padding: 8, lineHeight: 1.8 }}>{e.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>کدهای HTTP</Typography>
            {DP_HTTP_CODES.map((h) => <Row key={h.code} label={h.code} value={h.meaning} />)}
            <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 2, mb: 1 }}>انواع تیکت (type) و درگاه (paymentGateway)</Typography>
            {Object.entries(DP_TICKET_TYPE_LABELS).map(([k, v]) => <Row key={k} label={`type ${fa(k)}`} value={v} />)}
            <Row label="paymentGateway" value="0 = IPG · 1 = DPG · 3 = WALLET · 4 = CPG (اعتباری)" />
            <Row label="مقصد ریفاند (destinationType)" value="0 = کارت · 1 = شبا · 2 = کیف پول · 3 = اعتبار" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>کدهای PSP (درگاه‌های بانکی پشت دیجی‌پی)</Typography>
            {DP_PSP_CODES.map((p) => <Row key={p.code} label={<Mono>{p.code}</Mono> as any} value={p.name} />)}
            <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 2, mb: 1 }}>سبد خرید اعتباری (basketDetailsDto)</Typography>
            <Typography variant="body2" sx={{ lineHeight: 2 }}>
              فقط برای اقساطی/اعتباری الزامی است. هر قلم: sellerId و supplierId = فروشگاه، productCode = شناسهٔ محصول، brand = نام فروشگاه، count = تعداد، productType (۱ بادوام · ۲ مصرفی · ۳ خدمات · ۴ مصرفی‌بادوام) و categoryId (طبق فهرست دسته‌های دیجی‌پی) از تب تنظیمات.
              splitDetailsList (تسهیم بیمه/دیجی‌پلاس) مخصوص فروشگاه‌های فیزیکی است و در کازماشاپ استفاده نمی‌شود.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  // ---------------------------------------------------------------- layout

  return (
    <Box>
      <PageHeader
        title="مرکز دیجی‌پی"
        subtitle="مدیریت درگاه یکپارچهٔ دیجی‌پی (UPG): وضعیت اتصال، تنظیمات و کارمزد اقساطی، سفارش‌ها و عملیات reverse / refund / تحویل اعتباری، بازگشت‌های وجه، لاگ‌ها و ابزارهای تست"
        breadcrumbs={[{ label: 'مالی' }, { label: 'مرکز دیجی‌پی' }]}
        actions={[{ label: 'تنظیمات و کارمزد', onClick: () => navigate('/settings?tab=digipay'), icon: <SettingsIcon />, variant: 'outlined' }, { label: 'به‌روزرسانی', onClick: () => { loadOverview(); if (tab === 1) loadOrders(); if (tab === 2) loadRefunds(); if (tab === 3) loadLogs(); }, icon: <RefreshIcon />, variant: 'outlined' }]}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <DpLogo size={34} />
          {overview && (
            <>
              <Chip size="small" label={overview.environment === 'staging' ? 'محیط تست (uat)' : 'محیط لایو'} color={overview.environment === 'staging' ? 'warning' : 'success'} />
              <Chip size="small" icon={overview.configured ? <CheckCircleIcon /> : <WarningAmberIcon />} label={overview.configured ? 'اعتبارنامه کامل' : 'اعتبارنامه ناقص'} color={overview.configured ? 'success' : 'error'} variant="outlined" />
              <Chip size="small" label={overview.settings.enabled ? 'فعال برای کاربران' : 'خاموش برای کاربران'} color={overview.settings.enabled ? 'primary' : 'default'} variant="outlined" />
              <Chip size="small" label={`کارمزد اقساطی ${fa(overview.settings.creditSurchargePercent)}٪`} variant="outlined" />
              <Chip size="small" icon={<BoltIcon />} label={`${fa(overview.paidLast24h)} پرداخت ۲۴h`} variant="outlined" />
            </>
          )}
        </Stack>
      </PageHeader>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSearchParams(v ? { tab: String(v) } : {}); }} variant="scrollable" scrollButtons="auto">
          <Tab label="نمای کلی" />
          <Tab label={`سفارش‌ها${overview?.total ? ` (${fa(overview.total)})` : ''}`} />
          <Tab label="بازگشت وجه" />
          <Tab label="لاگ‌ها" />
          <Tab label="ابزارها و تست" />
          <Tab label="راهنما و کدهای خطا" />
        </Tabs>
      </Paper>

      {tab === 0 && renderOverview()}
      {tab === 1 && renderOrders()}
      {tab === 2 && renderRefunds()}
      {tab === 3 && renderLogs()}
      {tab === 4 && renderTools()}
      {tab === 5 && renderGuide()}

      {renderDetailDialog()}
      {renderConfirmDialog()}
    </Box>
  );
};

export default DigipayCenter;
